import { createContext, useEffect, useState } from "react";
import useWebsocket from "../../hooks/useWebsocket";
import { get, isEmpty, size } from "lodash";
import { differenceInMinutes } from "date-fns";
import constants from "../../utils/constants";
import { useGetGateways } from "./services";
import { GatewayContextProps, GatewayData } from "./types";
import { UseQueryResult } from "react-query";
import { GatewayDataType, GatewaysResponse } from "./types/GetGatewayType";
import { useAuth } from "@qure/qure-auth";

export const GatewayContext = createContext<GatewayContextProps>(
  {} as GatewayContextProps
);

export const GatewayContextProvider = (props: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { children } = props;
  const { userInfo } = useAuth();
  const isLoggedIn = get(userInfo, ["authenticated"], false);

  const [gatewayData, setGatewayData] = useState<GatewayDataType[]>([]);

  const {
    data: gateway_data,
    refetch: refetchGatewayData,
    isLoading,
  }: UseQueryResult<GatewaysResponse, Error> = useGetGateways(isLoggedIn);

  const { wsData } = useWebsocket(`/gateways/${userInfo?.username}/`, !isEmpty(userInfo));

  const formatTimeDifference = (timeDifference: number) => {
    let formattedDifference;
    if (timeDifference < 60) {
      formattedDifference = `${timeDifference}m`;
    } else {
      const hours = Math.floor(timeDifference / 60);
      const minutes = timeDifference % 60;
      formattedDifference = `${hours}h ${minutes}m`;
    }
    return formattedDifference;
  };

  const modifyGatewayData = (gatewayData: GatewayDataType[]) => {
    const modified_data = gatewayData?.map((gw: GatewayDataType) => ({
      uuid: gw?.gateway?.uuid,
      gateway_name: gw?.gateway?.gateway_name,
      last_online: gw?.last_online,
      workspace_id: gw?.gateway?.workspace?.id,
      workspace_name: gw?.gateway?.workspace?.name,
      time_difference: differenceInMinutes(new Date(), new Date(gw?.last_online)),
      gateway_poc_phone: gw?.gateway?.gateway_poc_phone,
    }));
    const groupedData = modified_data?.reduce((acc: GatewayData[], item) => {
      const existingItem = acc.find(
        (group: GatewayData) => group.workspace_id === item.workspace_id
      );

      if (existingItem) {
        if (item.time_difference > 16) {
          existingItem.offline_gateway_count = existingItem.offline_gateway_count + 1;
        }
        existingItem.gateway.push({
          uuid: item.uuid,
          last_online: item.last_online,
          gateway_name: item.gateway_name,
          time_difference: item.time_difference,
          gateway_poc_phone: item?.gateway_poc_phone,
        });
      } else {
        acc.push({
          workspace_id: item.workspace_id,
          workspace_name: item.workspace_name,
          offline_gateway_count: item.time_difference > 16 ? 1 : 0,
          gateway: [
            {
              uuid: item.uuid,
              last_online: item.last_online,
              gateway_name: item.gateway_name,
              time_difference: item.time_difference,
              gateway_poc_phone: item?.gateway_poc_phone,
            },
          ],
        });
      }

      return acc;
    }, []);

    return groupedData;
  };

  const offlineGateway = (gatewayData: GatewayDataType[]) => {
    const offlineData: GatewayDataType[] = [];
    gatewayData?.map((gw: GatewayDataType) => {
      const timeDifference = differenceInMinutes(new Date(), new Date(gw.last_online));

      if (timeDifference >= constants.GATEWAY_TIME.TIME_DIFFERENCE) {
        offlineData.push(gw);
      }
    });
    return offlineData;
  };

  useEffect(() => {
    setGatewayData(gateway_data?.gateways || []);
  }, [gateway_data]);

  useEffect(() => {
    if (wsData) {
      if (wsData.action === "gateway_status_update") {
        const gatewayContent = get(wsData, ["content", "gateways"], []);
        setGatewayData(gatewayContent);
      }
    }
  }, [wsData]);

  return (
    <GatewayContext.Provider
      value={{
        gatewayData: modifyGatewayData(gatewayData),
        offlineGatewayData: modifyGatewayData(offlineGateway(gatewayData)),
        totalGateway: size(gatewayData),
        offlineGatewayCount: size(offlineGateway(gatewayData)),
        formatTimeDifference: formatTimeDifference,
        refetchGatewayData: refetchGatewayData,
        isLoading: isLoading,
      }}
    >
      {children}
    </GatewayContext.Provider>
  );
};
